import { clamp } from "../core/math";

const FRAME_WINDOW = 600;

export interface MetricSummary {
  readonly fps: number;
  readonly frameAverageMs: number;
  readonly frameP95Ms: number;
  readonly frameP99Ms: number;
  readonly sampleCount: number;
}

export class Metrics {
  private readonly frameTimes = new Float32Array(FRAME_WINDOW);
  private frameIndex = 0;
  private frameCount = 0;
  private elapsedForFps = 0;
  private framesForFps = 0;
  private currentFps = 60;
  private summaryDirty = true;
  private cachedSummary: MetricSummary = {
    fps: 60,
    frameAverageMs: 0,
    frameP95Ms: 0,
    frameP99Ms: 0,
    sampleCount: 0,
  };

  addFrame(deltaSeconds: number): void {
    const bounded = clamp(deltaSeconds * 1000, 0, 250);
    this.frameTimes[this.frameIndex] = bounded;
    this.frameIndex = (this.frameIndex + 1) % FRAME_WINDOW;
    this.frameCount = Math.min(FRAME_WINDOW, this.frameCount + 1);
    this.elapsedForFps += deltaSeconds;
    this.framesForFps += 1;
    if (this.elapsedForFps >= 0.5) {
      this.currentFps = this.framesForFps / this.elapsedForFps;
      this.elapsedForFps = 0;
      this.framesForFps = 0;
      this.summaryDirty = true;
    }
  }

  reset(): void {
    this.frameTimes.fill(0);
    this.frameIndex = 0;
    this.frameCount = 0;
    this.elapsedForFps = 0;
    this.framesForFps = 0;
    this.currentFps = 60;
    this.summaryDirty = true;
    this.cachedSummary = {
      fps: 60,
      frameAverageMs: 0,
      frameP95Ms: 0,
      frameP99Ms: 0,
      sampleCount: 0,
    };
  }

  summary(): MetricSummary {
    if (!this.summaryDirty) {
      return this.cachedSummary;
    }
    if (this.frameCount === 0) {
      this.cachedSummary = {
        fps: this.currentFps,
        frameAverageMs: 0,
        frameP95Ms: 0,
        frameP99Ms: 0,
        sampleCount: 0,
      };
      this.summaryDirty = false;
      return this.cachedSummary;
    }
    const values = Array.from(this.frameTimes.slice(0, this.frameCount)).sort((a, b) => a - b);
    const average = values.reduce((total, value) => total + value, 0) / values.length;
    const percentile = (ratio: number): number => {
      const index = Math.min(values.length - 1, Math.floor(values.length * ratio));
      return values[index] ?? 0;
    };
    this.cachedSummary = {
      fps: this.currentFps,
      frameAverageMs: average,
      frameP95Ms: percentile(0.95),
      frameP99Ms: percentile(0.99),
      sampleCount: values.length,
    };
    this.summaryDirty = false;
    return this.cachedSummary;
  }
}
